import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, FeatureGroup, Polygon, Popup } from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import * as turf from "@turf/turf";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { baseurladdress } from "../baseurl";

const AddRegion = () => {
  const featureGroupRef = useRef(null);
  const navigate = useNavigate();
  const accesstoken = localStorage.getItem("token");
  const accessuserid = localStorage.getItem("id");
  
  const [regionName, setRegionName] = useState("");
  const [polygon, setPolygon] = useState([]);
  const [existingRegions, setExistingRegions] = useState([]);
  const [allAreas, setAllAreas] = useState([]);

  /* 🔹 Load existing regions */
  useEffect(() => {
    fetch(baseurladdress + "region/getregions", {
      headers: {
        Authorization: `Bearer ${accesstoken}`,
      },
    })
      .then(res => res.json())
      .then(data => {
        if (data.status === "Success") {
          setExistingRegions(data.regions || []);
        }
      })
      .catch(err => console.error("Error loading regions:", err));
  }, [accesstoken]);

  /* 🔹 Load all areas to show count under each region */
  useEffect(() => {
    fetch(baseurladdress + "area/getareas", {
      headers: {
        Authorization: `Bearer ${accesstoken}`,
      },
    })
      .then(res => res.json())
      .then(data => {
        if (data.status === "Success") {
          setAllAreas(data.areas || []);
        }
      })
      .catch(err => console.error("Error loading areas:", err));
  }, [accesstoken]);

  /* 🔹 Get areas under a specific region */
  const getAreasForRegion = (regionId) => {
    return allAreas.filter(area => area.aregion_id === regionId);
  };

  /* 🔹 When polygon is drawn */
  const onCreated = (e) => {
    const layer = e.layer;
    const latlngs = layer.getLatLngs()[0];
    const newCoords = latlngs.map(p => [p.lng, p.lat]);
    newCoords.push(newCoords[0]);
    const newPolygon = turf.polygon([newCoords]);

    /* 🔴 Overlap check with existing regions */
    for (const region of existingRegions) {
      const coords = JSON.parse(region.region_polygon).map(p => [p.lng, p.lat]);
      coords.push(coords[0]);
      const existingPolygon = turf.polygon([coords]);

      if (turf.booleanIntersects(newPolygon, existingPolygon)) {
        Swal.fire({
          icon: "error",
          title: "Overlap detected",
          text: `This region overlaps with "${region.region_name}"`,
        });
        featureGroupRef.current.removeLayer(layer);
        return;
      }
    }

    // allow only one polygon
    featureGroupRef.current.clearLayers();
    featureGroupRef.current.addLayer(layer);
    setPolygon(latlngs.map(p => ({ lat: p.lat, lng: p.lng })));
  };

  /* 🔹 Submit */
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!regionName || polygon.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Missing data",
        text: "Please enter region name and draw region on map",
      });
      return;
    }

    fetch(baseurladdress + "region/addregion", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accesstoken}`,
      },
      body: JSON.stringify({
        region_name: regionName,
        polygon,
        userid: accessuserid,
      }),
    })
      .then(res => res.json())
      .then(result => {
        if (result.status === "Success") {
          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "success",
            title: "Region added successfully",
            showConfirmButton: false,
            timer: 1800,
          });

          /* 🔹 RESET */
          setRegionName("");
          setPolygon([]);
          if (featureGroupRef.current) {
            featureGroupRef.current.clearLayers();
          }
          
          // Reload regions to show the new one
          fetch(baseurladdress + "region/getregions", {
            headers: {
              Authorization: `Bearer ${accesstoken}`,
            },
          })
            .then(res => res.json())
            .then(data => {
              if (data.status === "Success") {
                setExistingRegions(data.regions || []);
              }
            });
        } else {
          Swal.fire("Error", result.message || "Unable to add region", "error");
        }
      })
      .catch(err => {
        Swal.fire("Error", "Failed to add region", "error");
        console.error(err);
      });
  };

  // Responsive styles
  const containerStyle = {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "15px",
  };

  const headingStyle = {
    fontSize: "clamp(1.5rem, 4vw, 2rem)",
    marginBottom: "20px",
    color: "#333",
  };

  const formGroupStyle = {
    marginBottom: "20px",
  };

  const labelStyle = {
    display: "block",
    marginBottom: "8px",
    fontWeight: "600",
    fontSize: "clamp(0.9rem, 2vw, 1rem)",
    color: "#555",
  };

  const inputStyle = {
    width: "100%",
    maxWidth: "500px",
    padding: "12px",
    fontSize: "16px", // Prevents zoom on iOS
    borderRadius: "6px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
    transition: "border-color 0.3s",
  };

  const mapContainerStyle = {
    height: "clamp(300px, 50vh, 500px)",
    width: "100%",
    border: "1px solid #ccc",
    borderRadius: "6px",
    overflow: "hidden",
  };

  const buttonStyle = {
    marginTop: "20px",
    padding: "12px 24px",
    fontSize: "16px",
    fontWeight: "600",
    backgroundColor: (!regionName || polygon.length === 0) ? "#ccc" : "#28a745",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: (!regionName || polygon.length === 0) ? "not-allowed" : "pointer",
    transition: "background-color 0.3s",
    width: "100%",
    maxWidth: "300px",
  };

  return (
    <div style={containerStyle}>
      <h3 style={headingStyle}>Add Region</h3>
      <form onSubmit={handleSubmit}>
        {/* Region Name */}
        <div style={formGroupStyle}>
          <label htmlFor="regionName" style={labelStyle}>
            Region Name *
          </label>
          <input
            id="regionName"
            type="text"
            placeholder="Region Name (eg: North Kerala)"
            value={regionName}
            onChange={(e) => setRegionName(e.target.value)}
            style={inputStyle}
          />
        </div>

        {/* Map */}
        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Draw Region on Map * <span style={{ fontSize: "0.85em", color: "#666" }}>(Click existing regions to view details)</span>
          </label>
          <MapContainer
            center={[9.9312, 76.2673]}
            zoom={13}
            style={mapContainerStyle}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

            {/* Existing regions with popup */}
            {existingRegions.map(region => {
              const areasInRegion = getAreasForRegion(region.region_id);
              
              return (
                <Polygon
                  key={region.region_id}
                  positions={JSON.parse(region.region_polygon).map(p => [p.lat, p.lng])}
                  pathOptions={{ color: "blue", fillOpacity: 0.2 }}
                >
                  <Popup>
                    <div style={{ minWidth: "180px", maxWidth: "300px" }}>
                      <h4 style={{ margin: "0 0 10px 0", color: "#333", fontSize: "1rem" }}>
                        {region.region_name}
                      </h4>
                      
                      <div style={{ marginBottom: "8px", fontSize: "0.9rem" }}>
                        <strong>Region Manager:</strong> {region.region_manager_name || "Not Assigned"}
                      </div>

                      <div style={{ fontSize: "0.9rem" }}>
                        <strong>Areas:</strong>
                        {areasInRegion.length > 0 ? (
                          <ul style={{ margin: "5px 0 0 0", paddingLeft: "20px", color: "#666" }}>
                            {areasInRegion.map(area => (
                              <li key={area.area_id} style={{ marginBottom: "3px" }}>
                                {area.area_name}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div style={{ color: "#999", marginTop: "2px", fontStyle: "italic" }}>
                            No areas added yet
                          </div>
                        )}
                      </div>
                    </div>
                  </Popup>
                </Polygon>
              );
            })}

            {/* Draw new region */}
            <FeatureGroup ref={featureGroupRef}>
              <EditControl
                position="topright"
                onCreated={onCreated}
                draw={{
                  polygon: true,
                  rectangle: false,
                  polyline: false,
                  circle: false,
                  marker: false,
                  circlemarker: false,
                }}
              />
            </FeatureGroup>
          </MapContainer>
        </div>

        <button
          type="submit"
          style={buttonStyle}
          disabled={!regionName || polygon.length === 0}
          onMouseEnter={(e) => {
            if (regionName && polygon.length > 0) {
              e.target.style.backgroundColor = "#218838";
            }
          }}
          onMouseLeave={(e) => {
            if (regionName && polygon.length > 0) {
              e.target.style.backgroundColor = "#28a745";
            }
          }}
        >
          Save Region
        </button>
      </form>

      <style>{`
        @media (max-width: 768px) {
          .leaflet-control-container .leaflet-top.leaflet-right {
            top: 10px;
            right: 10px;
          }
        }
        
        input:focus {
          outline: none;
          border-color: #28a745;
          box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1);
        }
      `}</style>
    </div>
  );
};

export default AddRegion;